/**
 * Enhanced Video Optimization Script
 * Implements progressive enhancement for video loading
 */

// Connection speed detection
function getConnectionSpeed() {
    // Check if Network Information API is available
    if (navigator.connection) {
        const connection = navigator.connection;

        // Return connection type if available
        if (connection.effectiveType) {
            console.log('Connection type: ' + connection.effectiveType);
            return connection.effectiveType; // '4g', '3g', '2g', or 'slow-2g'
        }
    }

    // Default to '3g' if Network Information API is not available
    return '3g';
}

// Determine if we should load video based on connection speed
function shouldLoadVideo() {
    // Get connection speed (for future use)
    // const connectionSpeed = getConnectionSpeed();

    // For now, always load videos to ensure they play
    // Later we can refine this based on connection speed
    return true;

    // Original logic (commented out):
    // return connectionSpeed === '4g';
}

// Create and load video element based on connection speed
function createVideoElement(container, isMuted = true) {
    // Get video URLs from data attributes
    const mobileVideoUrl = container.dataset.mobileVideo;
    const desktopVideoUrl = container.dataset.desktopVideo;
    const posterUrl = container.dataset.poster;
    const isMutedAttr = container.dataset.muted === 'true';
    const hasMobileVideo = container.dataset.hasMobileVideo === 'true';

    // Create video element
    const video = document.createElement('video');
    video.className = 'video--fill';
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('preload', 'auto');

    // Set muted attribute if needed
    if (isMuted || isMutedAttr) {
        video.muted = true; // Use property instead of attribute for better compatibility
    }

    // Set poster if available
    if (posterUrl) {
        video.setAttribute('poster', posterUrl);
    }

    // Set Plyr config
    video.setAttribute('data-plyr-config', '{"controls": []}');

    // Create source elements based on screen size
    const mobileSource = document.createElement('source');
    mobileSource.setAttribute('media', '(max-width: 767px)');
    mobileSource.setAttribute('type', 'video/mp4');

    // If no mobile video is available, use desktop video as fallback
    if (hasMobileVideo) {
        mobileSource.setAttribute('src', mobileVideoUrl);
    } else {
        mobileSource.setAttribute('src', desktopVideoUrl);
    }

    const desktopSource = document.createElement('source');
    desktopSource.setAttribute('media', '(min-width: 768px)');
    desktopSource.setAttribute('type', 'video/mp4');
    desktopSource.setAttribute('src', desktopVideoUrl);

    // Append sources to video
    video.appendChild(mobileSource);
    video.appendChild(desktopSource);

    // Add event listener for when video can play
    video.addEventListener('canplay', function () {
        video.classList.add('loaded');

        // Ensure video plays
        const playPromise = video.play();

        if (playPromise !== undefined) {
            playPromise.catch(error => {
                console.log('Auto-play was prevented. This is expected in some browsers:', error);
                // We'll show the poster image instead
            });
        }

        // Fade out poster images when video is ready
        const posterElements = container.parentElement.querySelectorAll('.hero-poster-mobile, .hero-poster-desktop, .hero-poster');
        if (posterElements.length > 0) {
            posterElements.forEach(poster => {
                // Only fade out posters that are currently visible (not hidden by CSS)
                const computedStyle = window.getComputedStyle(poster);
                if (computedStyle.display !== 'none') {
                    poster.style.opacity = '0';
                }
            });
        }
    });

    // Handle loading errors
    video.addEventListener('error', function (e) {
        console.error('Video loading error:', e);
        // Keep poster visible in case of error
    });

    // Handle video metadata loaded to set proper dimensions
    video.addEventListener('loadedmetadata', function () {
        // Get the natural aspect ratio of the video
        const aspectRatio = video.videoWidth / video.videoHeight;

        // Apply the aspect ratio to the video element on mobile only
        if (aspectRatio > 0) {
            // Check if we're on mobile
            const isMobile = window.matchMedia('(max-width: 767px)').matches;

            if (isMobile) {
                // Set the aspect ratio using CSS custom property for mobile
                video.style.setProperty('--video-aspect-ratio', `${aspectRatio}`);

                // Also apply to poster images
                const posterElements = container.parentElement.querySelectorAll('.hero-poster-mobile, .hero-poster-desktop, .hero-poster');
                if (posterElements.length > 0) {
                    posterElements.forEach(poster => {
                        poster.style.setProperty('--video-aspect-ratio', `${aspectRatio}`);
                    });
                }
            }
        }
    });

    // Append video to container
    container.appendChild(video);

    // Try to play the video immediately
    setTimeout(() => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
            playPromise.catch(error => {
                console.log('Initial auto-play was prevented:', error);
            });
        }
    }, 0);

    return video;
}

// Initialize hero videos with progressive enhancement
function initHeroVideos() {
    const heroVideos = document.querySelectorAll('.section-hero-video video, .section-hero-video-sound video');

    if (heroVideos.length === 0) return;

    heroVideos.forEach(video => {
        // Add loaded class when video can play
        video.addEventListener('canplay', function () {
            video.classList.add('loaded');
        });

        // Handle aspect ratio for mobile videos
        video.addEventListener('loadedmetadata', function () {
            const isMobile = window.matchMedia('(max-width: 767px)').matches;
            const isVideoMobile = video.classList.contains('video--mobile');

            if (isMobile && isVideoMobile) {
                const aspectRatio = video.videoWidth / video.videoHeight;
                if (aspectRatio > 0) {
                    video.style.setProperty('--video-aspect-ratio', `${aspectRatio}`);
                }
            }
        });

        // Initialize Plyr if available
        if (window.Plyr && !video.hasAttribute('data-plyr-initialized')) {
            video.setAttribute('data-plyr-initialized', 'true');
            new Plyr(video);
        }

        // Try to play the video
        const playPromise = video.play();
        if (playPromise !== undefined) {
            playPromise.catch(error => {
                console.log('Hero video auto-play was prevented:', error);
            });
        }
    });
}

// Handle Instagram video loading with progressive enhancement
function optimizeInstagramVideos() {
    const instagramContainers = document.querySelectorAll('.instagram-video-container');

    if (instagramContainers.length === 0) return;

    // Set up Intersection Observer
    const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const container = entry.target;
                const video = container.querySelector('video');

                if (video) {
                    // Check if video has data-src instead of src
                    const source = video.querySelector('source[data-src]');
                    if (source) {
                        // Set the src attribute from data-src
                        source.setAttribute('src', source.getAttribute('data-src'));
                        source.removeAttribute('data-src');

                        // Load the video
                        video.load();

                        // Add event listener for when video can play
                        video.addEventListener('canplay', function () {
                            video.classList.add('loaded');

                            // Try to play the video
                            const playPromise = video.play();
                            if (playPromise !== undefined) {
                                playPromise.catch(error => {
                                    console.log('Instagram video auto-play was prevented:', error);
                                });
                            }

                            // Fade out poster image
                            const posterElement = container.querySelector('.instagram-poster');
                            if (posterElement) {
                                posterElement.style.opacity = '0';
                            }
                        });

                        // Handle errors
                        video.addEventListener('error', function (e) {
                            console.error('Instagram video loading error:', e);
                            // Keep poster visible in case of error
                        });
                    } else {
                        // Try to play the video directly
                        const playPromise = video.play();
                        if (playPromise !== undefined) {
                            playPromise.catch(error => {
                                console.log('Instagram video auto-play was prevented:', error);
                            });
                        }
                    }
                }

                // Stop observing once handled
                observer.unobserve(container);
            }
        });
    }, {
        root: null,
        rootMargin: '50px 0px',
        threshold: 0.1
    });

    // Start observing all Instagram containers
    instagramContainers.forEach(container => {
        observer.observe(container);
    });
}

// Handle lazy loading of images with data-bg attribute
function lazyLoadBackgrounds() {
    const lazyBackgrounds = document.querySelectorAll('[data-bg]');

    if (lazyBackgrounds.length === 0) return;

    // Set up Intersection Observer
    const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const element = entry.target;
                const bgUrl = element.getAttribute('data-bg');

                if (bgUrl && bgUrl !== 'null' && bgUrl !== 'undefined') {
                    // Set background image
                    element.style.backgroundImage = `url('${bgUrl}')`;
                    element.removeAttribute('data-bg');

                    // Stop observing
                    observer.unobserve(element);
                } else {
                    // If bgUrl is null, undefined, or "null", remove the data-bg attribute
                    // to prevent setting background-image to url("null")
                    element.removeAttribute('data-bg');
                    observer.unobserve(element);
                }
            }
        });
    }, {
        root: null,
        rootMargin: '50px 0px',
        threshold: 0.1
    });

    // Start observing all elements with data-bg
    lazyBackgrounds.forEach(element => {
        observer.observe(element);
    });
}

// Handle lazy loading of images with data-src attribute
function lazyLoadImages() {
    const lazyImages = document.querySelectorAll('img.lazy-image[data-src]');

    if (lazyImages.length === 0) return;

    // Set up Intersection Observer
    const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const img = entry.target;
                const src = img.getAttribute('data-src');

                if (src) {
                    img.setAttribute('src', src);
                    img.removeAttribute('data-src');
                    img.classList.remove('lazy-image');

                    // Stop observing
                    observer.unobserve(img);
                }
            }
        });
    }, {
        root: null,
        rootMargin: '50px 0px',
        threshold: 0.1
    });

    // Start observing all lazy images
    lazyImages.forEach(img => {
        observer.observe(img);
    });
}

// Handle twin-video-brand videos
function optimizeTwinBrandVideos() {
    console.log('Initializing twin brand videos');
    const brandVideos = document.querySelectorAll('.twin-video-brand .home-brand-video .video--fill');

    if (brandVideos.length === 0) {
        console.log('No twin brand videos found');
        return;
    }

    console.log(`Found ${brandVideos.length} twin brand videos`);

    // Immediately try to load and play all videos
    brandVideos.forEach(video => {
        // Force preload
        video.preload = 'auto';

        // Make sure autoplay is enabled
        video.autoplay = true;

        // Make sure loop is enabled
        video.loop = true;

        // Make sure it's muted (required for autoplay)
        video.muted = true;

        // Add canplay event listener
        video.addEventListener('canplay', function onCanPlay() {
            console.log('Twin brand video can play');
            video.classList.add('loaded');

            // Fade out poster
            const homeVideoContainer = video.closest('.home-brand-video');
            if (homeVideoContainer) {
                const poster = homeVideoContainer.querySelector('.hero-poster');
                if (poster) {
                    poster.style.opacity = '0';
                }
            }

            // Remove the event listener to avoid multiple calls
            video.removeEventListener('canplay', onCanPlay);
        });

        // Add error event listener
        video.addEventListener('error', function (e) {
            console.error('Twin brand video error:', e);
        });

        // Force load the video
        video.load();

        // Try to play immediately
        setTimeout(() => {
            const playPromise = video.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        console.log('Twin brand video playing successfully');
                        video.classList.add('loaded');
                    })
                    .catch(error => {
                        console.error('Twin brand video autoplay prevented:', error);

                        // Try again with user interaction simulation
                        document.addEventListener('click', function playOnClick() {
                            video.play()
                                .then(() => {
                                    console.log('Twin brand video playing after user interaction');
                                    video.classList.add('loaded');
                                })
                                .catch(err => console.error('Still cannot play video:', err));

                            // Remove the event listener after first click
                            document.removeEventListener('click', playOnClick);
                        }, { once: true });
                    });
            }
        }, 100);
    });
}

// Initialize all optimizations
function initVideoOptimizations() {
    // First load all background images immediately
    lazyLoadBackgrounds();

    // Then initialize videos with progressive enhancement
    initHeroVideos();

    // Optimize twin brand videos
    optimizeTwinBrandVideos();

    // Optimize Instagram videos
    optimizeInstagramVideos();

    // Lazy load images
    lazyLoadImages();

    // Listen for network changes and update video loading strategy
    if (navigator.connection) {
        navigator.connection.addEventListener('change', function () {
            console.log('Connection type changed to ' + navigator.connection.effectiveType);
            // You could reload videos here if connection improves
        });
    }
}

// Run on DOMContentLoaded
document.addEventListener('DOMContentLoaded', initVideoOptimizations);
